
import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash, BarChart2, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { useCountry } from '@/contexts/country'; // Updated import path
import { CardItem } from './CardForm';
import { Link } from 'react-router-dom';
import useCategories from '@/hooks/useCategories';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';

interface CardTableProps {
  cards: CardItem[];
  isLoading: boolean;
  onEdit: (card: CardItem) => void;
  onDelete: (id: string) => void;
}

const CardTable: React.FC<CardTableProps> = ({ cards, isLoading, onEdit, onDelete }) => {
  const { availableCountries } = useCountry();
  const { categories } = useCategories(true);
  const isMobile = useIsMobile();

  const getCategoryName = (categoryId: string | undefined) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-500">Published</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'archived':
        return <Badge className="bg-gray-500">Archived</Badge>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading cards...</div>;
  }

  // Mobile view - card layout
  if (isMobile) {
    if (cards.length === 0) {
      return (
        <div className="text-center py-6 bg-white rounded-md border p-8">
          <p className="text-gray-500 mb-2">No insight cards found</p>
          <p className="text-gray-400 text-sm mb-4">Create your first insight card to start sharing data with citizens</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {cards.map((card) => (
          <Card key={card.id} className="overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg font-medium">{card.title}</CardTitle>
                {getStatusBadge(card.status)}
              </div>
            </CardHeader>
            <CardContent className="pb-3 pt-0">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="font-medium">{getCategoryName(card.category_id)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Country:</span>
                  <span className="font-medium">{card.country_code ? 
                    availableCountries.find(c => c.code === card.country_code)?.name || card.country_code 
                    : 'Global'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Agreement:</span>
                  <span className="font-medium">{card.agreePercentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Responses:</span>
                  <span className="font-medium">{card.totalPollsters}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Insights:</span>
                  <span className="font-medium">{card.insights?.length || 0}</span>
                </div>
                {card.slug && (
                  <div className="flex justify-between items-center border-t pt-2 mt-2">
                    <span className="text-gray-500">View:</span>
                    <Link 
                      to={`/insight/${card.slug}`}
                      className="text-nation-blue hover:text-nation-blue-dark flex items-center gap-1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="text-xs font-mono">{card.slug}</span>
                      <ExternalLink size={14} />
                    </Link>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between pt-2 pb-3 bg-gray-50 dark:bg-gray-800/50">
              <Button variant="ghost" size="sm" onClick={() => onEdit(card)}>
                <Edit size={16} className="mr-1" /> Edit
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(card.id)}>
                <Trash size={16} className="mr-1" /> Delete
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop view - table layout
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Country</TableHead>
            <TableHead>Agreement</TableHead>
            <TableHead>Responses</TableHead>
            <TableHead>Insights</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cards.length > 0 ? (
            cards.map((card) => (
              <TableRow key={card.id}>
                <TableCell className="font-medium">{card.title}</TableCell>
                <TableCell>
                  {card.slug ? (
                    <div className="flex items-center gap-2">
                      <LinkIcon size={14} className="text-gray-400" />
                      <span className="text-sm font-mono">{card.slug}</span>
                      <Link 
                        to={`/insight/${card.slug}`}
                        className="text-nation-blue hover:text-nation-blue-dark"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink size={14} />
                      </Link>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">No slug</span>
                  )}
                </TableCell>
                <TableCell>{getCategoryName(card.category_id)}</TableCell>
                <TableCell>
                  {card.country_code ? 
                    availableCountries.find(c => c.code === card.country_code)?.name || card.country_code 
                    : 'Global'}
                </TableCell>
                <TableCell>{card.agreePercentage}%</TableCell>
                <TableCell>{card.totalPollsters}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <BarChart2 size={16} className="text-gray-400 mr-1" /> 
                    <span>{card.insights?.length || 0}</span>
                  </div>
                </TableCell>
                <TableCell>{getStatusBadge(card.status)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(card)}>
                      <Edit size={16} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onDelete(card.id)}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-4 text-gray-500">
                No insight cards found. Create your first card!
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default CardTable;
